/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import { useEffect, useState } from 'react';

/** Reads the visible browser height without changing the initial SSR render. */
export function useVisualViewportHeight(enabled: boolean) {
  const [height, setHeight] = useState<number>();

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const viewport = window.visualViewport;
    let frameId: number | null = null;
    const updateHeight = () => {
      // Pinch zoom should magnify the page, not resize its layout.
      if (viewport && viewport.scale !== 1) return;
      const nextHeight = viewport?.height || window.innerHeight;
      if (Number.isFinite(nextHeight) && nextHeight > 0) {
        setHeight(nextHeight);
      }
    };

    const scheduleUpdate = () => {
      if (frameId !== null) return;

      // Coalesce viewport and focus events into one measurement before paint.
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateHeight();
      });
    };

    updateHeight();
    viewport?.addEventListener('resize', scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('orientationchange', scheduleUpdate);
    window.addEventListener('focusin', scheduleUpdate);
    window.addEventListener('focusout', scheduleUpdate);
    window.addEventListener('pageshow', scheduleUpdate);

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      viewport?.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('orientationchange', scheduleUpdate);
      window.removeEventListener('focusin', scheduleUpdate);
      window.removeEventListener('focusout', scheduleUpdate);
      window.removeEventListener('pageshow', scheduleUpdate);
    };
  }, [enabled]);

  return enabled ? height : undefined;
}
